import { useMemo, useCallback } from "react";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import { useInputMode } from "../../utils/hooks/useInputMode";
import OptimizedImage from "../common/OptimizedImage";
import LazyRow from "../common/LazyRow";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

// ============================================================================
// CONSTANTS
// ============================================================================

// Number of items per row
var ITEMS_PER_ROW = 5;

/**
 * Chunks an array into smaller arrays of specified size
 */
function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

var MovieItem = function (props) {
  var item = props.item || {};
  var onEnterPress = props.onEnterPress;

  var contentPhotos = item.contentPhotos;
  var tags = item.tags;
  var contentType = item.contentType;
  var _id = item._id;
  var kind = item.kind;
  var createdAt = item.createdAt;

  var focusableResult = useFocusable({
    onEnterPress: onEnterPress,
    focusKey: "content_" + _id + "_" + createdAt,
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  var isSeries = contentType?.kind === "series" || kind === "series";

  // Use optimized image with LQIP and WebP support (w=200, q=90)
  var imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });

  return (
    <div className="legacy-child-grid-item">
      {isSeries && (
        <div className="legacy-child-grid-series-decoration">
          <img src="/serial_pseudo.svg" alt="" />
        </div>
      )}
      <div
        ref={ref}
        className={"legacy-child-grid-card-outer" + (focused ? " is-focused" : "")}
        onMouseEnter={handleMouseEnter}
        onClick={onEnterPress}
      >
        <div className="legacy-child-grid-card">
          <div className="legacy-child-grid-image">
            <OptimizedImage
              src={imageData.src}
              alt={item.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
          {tags?.[0] && <ContentTag label={tags[0]} />}
          <div className="legacy-child-grid-overlay">
            <span className="legacy-child-grid-title">{item.title}</span>
            <span className="legacy-child-grid-year">{item.year}</span>
          </div>
        </div>
        <div className="legacy-child-grid-border" />
      </div>
    </div>
  );
};

/**
 * Single row of movie items with its own focus context
 */
var MovieRow = function (props) {
  var items = props.items;
  var onFocus = props.onFocus;
  var navigate = props.navigate;
  var nextPlayGuide = props.nextPlayGuide;

  var focusableResult = useFocusable({
    onFocus: function (focusDetails) {
      if (onFocus && focusableResult.ref.current) {
        var rect = focusableResult.ref.current.getBoundingClientRect();
        onFocus({
          ...focusDetails,
          top: rect.top,
          y: rect.top,
          rowHeight: rect.height,
          itemHeight: rect.height,
          height: rect.height,
        });
      }
    },
  });
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-child-grid-row-container" ref={ref}>
        {items.map(function (item, index) {
          var contentKind = item.contentType?.kind || "movie";
          return (
            <MovieItem
              item={item}
              key={item._id + "_" + item.createdAt + '_' + index}
              onEnterPress={function () {
                var path = "/" + contentKind + "/" + item.slug;
                if (contentKind === "music") {
                  navigate(path, {
                    state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
                  });
                } else {
                  navigate(path);
                }
              }}
            />
          );
        })}
      </div>
    </FocusContext.Provider>
  );
};

export var CategoriesSlider = function (props) {
  var movies = props.movies;
  var onFocus = props.onFocus;
  var nextPlayGuide = props.nextPlayGuide;

  var focusableResult = useFocusable();
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  var navigate = useNavigate();

  // Chunk items into rows
  var chunkedItems = useMemo(
    function () {
      if (!movies || movies.length === 0) return [];
      return chunkArray(movies, ITEMS_PER_ROW);
    },
    [movies]
  );

  if (!movies || movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-child-grid-row" ref={ref}>
        {chunkedItems.map(function (rowItems, rowIndex) {
          return (
            <LazyRow key={"row_" + rowIndex} index={rowIndex}>
              <MovieRow
                items={rowItems}
                onFocus={onFocus}
                navigate={navigate}
                nextPlayGuide={nextPlayGuide}
              />
            </LazyRow>
          );
        })}
      </div>
    </FocusContext.Provider>
  );
};
